import { useEffect } from 'react';
import type { RefObject } from 'react';

// [disabled] elements are intentionally excluded — Save starts out disabled
// on every modal this hook wraps, and including it would give the trap a
// default-state escape hatch via Cancel (Tab would still land on Save).
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Wraps Tab/Shift+Tab focus within `containerRef` while `active` is true.
 * Only the boundary case is intercepted — Tab past the last focusable
 * element wraps to the first, Shift+Tab past the first wraps to the last —
 * so normal in-between tabbing is left to the browser. A container with no
 * focusable elements is a no-op.
 */
export function useFocusTrap(containerRef: RefObject<HTMLElement | null>, active: boolean) {
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement;

      if (e.shiftKey) {
        if (current === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (current === last) {
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', onKeyDown);
    return () => container.removeEventListener('keydown', onKeyDown);
  }, [containerRef, active]);
}
